<template>
  <div class="billing-wrapper">
    <form method="post" autocomplete="nope" @submit.prevent="purchase">
      <div class="billing-container">
        <h3 class="section-title">
          {{ label.get("billing_information") }}
        </h3>
        <div class="atto-form">
          <div class="field-row">
            <div
              class="field-wrap"
              :class="{ 'has-error': error && !$v.company_name.required }"
            >
              <p class="label">
                {{ label.get("company") }} <i class="asterix">*</i>
              </p>
              <input
                v-model="company_name"
                type="text"
                name="company_name"
                placeholder="Acme, Inc"
              />
            </div>
            <div class="field-wrap">
              <p class="label">
                {{ label.get("full_name") }}
              </p>
              <input
                v-model="name"
                type="text"
                name="name"
                placeholder="John Doe"
              />
            </div>
          </div>
          <div class="field-row">
            <div class="half">
              <div
                class="field-wrap"
                :class="{ 'has-error': error && !$v.address.required }"
              >
                <p class="label">
                  {{ label.get("address") }} <i class="asterix">*</i>
                </p>
                <input
                  v-model="address"
                  type="text"
                  name="address"
                  placeholder="Lombard St."
                  @input="onAddressChange"
                />
              </div>
            </div>
            <div class="half m-mb-0">
              <div
                class="field-wrap"
                :class="{ 'has-error': error && !$v.city.required }"
              >
                <p class="label">
                  {{ label.get("city") }}<i class="asterix">*</i>
                </p>
                <input
                  v-model="city"
                  type="text"
                  name="city"
                  placeholder="San Francisco"
                  @input="onCityChange"
                />
              </div>
              <div
                class="field-wrap"
                :class="{ 'has-error': error && $v.zip.$error }"
              >
                <p class="label">
                  {{ label.get("zip_code") }} <i class="asterix">*</i>
                </p>
                <input
                  v-model="zip"
                  type="text"
                  maxlength="10"
                  name="zip"
                  placeholder="94107"
                  @input="onZipChange"
                />
              </div>
            </div>
          </div>
          <div class="field-row">
            <div
              class="field-wrap"
              :class="{ 'has-error': error && $v.country_id.$error }"
            >
              <p class="label">
                {{ label.get("country") }} <i class="asterix">*</i>
              </p>
              <search-dropdown-component
                :value="country_id"
                :options="countryList"
                placeholder="Select..."
                @onOptionSelected="onCountrySelected"
                @onInputChange="onCountrySearch"
              />
            </div>
            <div class="field-wrap" v-if="country_id !== 187">
              <p class="label">
                {{ label.get("state_province") }}
              </p>
              <input
                v-model="state"
                type="text"
                name="state"
                placeholder="California"
              />
            </div>
            <div
              class="field-wrap"
              v-if="country_id === 187"
              :class="{ 'has-error': error && $v.state.$error }"
            >
              <p class="label">
                {{ label.get("state_province") }} <i class="asterix">*</i>
              </p>
              <search-dropdown-component
                :value="state"
                :options="stateList"
                placeholder="Select..."
                @onOptionSelected="onStateSelected"
                @onInputChange="onStateSearch"
              />
            </div>
          </div>
          <div class="field-row">
            <div
              class="field-wrap"
              :class="{ 'has-error': error && $v.card_holder.$error }"
            >
              <p class="label">
                {{ label.get("subscription_card_holder") }}
                <i class="asterix">*</i>
              </p>
              <input
                v-model="card_holder"
                type="text"
                name="card_holder"
                :placeholder="label.get('subscription_name_on_card')"
              />
            </div>
          </div>
          <div class="field-row no-mb">
            <div class="half">
              <div class="field-wrap">
                <p class="label">
                  {{ label.get("subscription_credit_card_number") }}
                  <i class="asterix">*</i>
                </p>
                <div id="card-number" />
              </div>
            </div>
            <div class="half">
              <div class="field-wrap">
                <p class="label">
                  {{ label.get("subscription_credit_card_expiration") }}
                  <i class="asterix">*</i>
                </p>
                <div id="card-expiry" />
              </div>
              <div class="field-wrap">
                <p class="label">
                  {{ label.get("subscription_credit_card_cvc") }}
                  <i class="asterix">*</i>
                </p>
                <div id="card-cvc" />
              </div>
            </div>
          </div>
          <div class="field-row">
            <div
              id="card-errors"
              class="error-message card-errors"
              role="alert"
            >
              {{ errorMessage }}
            </div>
          </div>
        </div>
      </div>
      <div class="billing-wrap">
        <div class="billing-sidebar">
          <h3 class="sidebar-title">
            {{ label.get("order_summary") }}
          </h3>
          <hr class="break-line" />
          <div
            class="field-row title-field"
            style="margin-bottom: 21px; margin-top: -4px;"
          >
            <h4 class="sidebar-subtitle">
              {{ label.get(plan) }} {{ label.get("billing") }}
            </h4>
            <button
              v-if="plan === 'monthly_9'"
              class="switch-plan"
              @click="
                changePlan($event, 'annual_95')
              "
            >
              {{
                label.get("switch_to_annual_monthly", {
                  changePlan: label.get("annual"),
                })
              }}
              <br />
              <b>{{ label.get("save") }} 20%</b>
            </button>
            <button
              v-if="plan === 'annual_95'"
              class="switch-plan monthly"
              @click="
                changePlan($event, 'monthly_9')
              "
            >
              {{
                label.get("switch_to_annual_monthly", {
                  changePlan: label.get("monthly"),
                })
              }}
            </button>
          </div>
          <div class="field-row">
            <h5 class="field-label">
              {{ label.get("total_users") }}
            </h5>
            <span class="value">{{ totalUsers }}</span>
          </div>
          <div class="field-row">
            <h5 class="field-label">
              {{ label.get("subscription_price_per_user") }}
            </h5>
            <span class="value">${{ pricePerUser }}/{{ planLabel }}</span>
          </div>
          <div class="field-row">
            <h5 class="field-label">
              {{ label.get("subscription_subtotal") }}
            </h5>
            <span class="value">${{ subTotal.toFixed(2) }}</span>
          </div>
          <div class="field-row">
            <h5 class="field-label">
              {{ label.get("subscription_tax") }}
            </h5>
            <span class="value">${{ taxAmount.toFixed(2) }}</span>
          </div>
          <!--<div class="field-row discount">
                      <h5 class="field-label">
                        Atto Discount
                      </h5>
                      <span class="value">${{ selectedPlan.pricePerUser }}%</span>
                    </div>-->
          <div class="field-row total">
            <h5 class="field-label">
              {{ label.get("subscription_total_amount") }}
            </h5>
            <span class="value">${{ totalAmount.toFixed(2) }}/{{ planLabel }}</span>
          </div>
          <hr class="break-line bottom" />
          <button
            class="atto-btn submit"
            :disabled="disabledCompletePurchaseBtn"
          >
            {{ label.get("subscription_complete_purchase") }}
          </button>
        </div>
        <div class="billing-info">
          <p
            v-html="
              label.get(
                'billing_information_complete_purchase_agree_description'
              )
            "
          />
          <p>
            {{
              label.get("billing_information_automatic_renewals_description")
            }}
          </p>
          <p
            v-html="
              label.get(
                'billing_information_you_will_be_charged_the_full_amount_immediately',
                { subscribedUntil: subscribedUntil }
              )
            "
          />
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { required, requiredIf } from "vuelidate/lib/validators";
import SubscriptionService from "../services/SubscriptionService";
import UtilsService from "../../../shared/services/UtilsService";
import SearchDropdownComponent from "../../../shared/components/SearchDropdownComponent";
import BillingService from "../../settings/billing/services/BillingService";
import Billing from "../../../shared/enums/billing";

let cardNumber = null;
export default {
  name: "BillingInformationComponent",
  components: {
    SearchDropdownComponent,
  },
  data() {
    const selectedPlan = localStorage.getItem("plan");
    const totalUsers = localStorage.getItem("quantity");

    const pricePerUserPerPlan = Billing.planValue(selectedPlan);

    const subTotalAmount = pricePerUserPerPlan * parseInt(totalUsers);
    const initialTaxAmount = 0;

    return {
      isSelected: false,
      countries: [],
      countryList: [],
      stateList: [],
      plan: selectedPlan,
      planLabel:
        Billing.isMonthly(selectedPlan)
          ? this.label.get("month")
          : this.label.get("year"),
      totalUsers: totalUsers,
      pricePerUser: pricePerUserPerPlan,
      subTotal: subTotalAmount,
      taxAmount: initialTaxAmount,
      totalAmount: subTotalAmount + initialTaxAmount,
      subscribedUntil:
          Billing.isMonthly(selectedPlan)
          ? this.$moment()
              .add(1, "month")
              .format("M/D/YYYY")
          : this.$moment()
              .add(1, "year")
              .format("M/D/YYYY"),
      company_name: this.$store.state.company.name,
      name: this.$store.state.user.name,
      clientSecret: null,
      city: "",
      zip: "",
      address: "",
      country_id: null,
      state: null,
      tax_id: "",
      card_holder: "",
      paymentMethod: "",
      error: false,
      errorMessage: "",
      debounce: null,
      disabledCompletePurchaseBtn: false,
      payInvoiceRetryCount: 1
    }
  },
  validations: {
    company_name: { required },
    city: { required },
    zip: { required },
    address: { required },
    country_id: { required },
    card_holder: { required },
    state: {
      required: requiredIf(function(data) {
        return data.country_id === 187;
      }),
    },
  },
  created() {
    window.scrollTo(0, 0);
    if (
      this.$store.state.subscribed ||
      !this.$store.state.permission.canAccessBilling
    ) {
      this.$router.push("/activity");
    }
  },
  mounted() {
    const plan = localStorage.getItem("plan");
    if (plan == null) {
      this.$router.push("/subscription/plans");
    }
    this.$store.commit("showLoading");
    SubscriptionService.getBillingInfo()
      .then((result) => {
        this.clientSecret = result.intent.client_secret;
        this.countries = result.countries.map((item) => {
          return {
            value: item.id,
            label: item.name,
            iso_code: item.iso_code
          };
        });
        this.countryList = this.countries;
        this.stateList = this.getStateList();
        const billingInfo = result.billingInformation;
        if (billingInfo !== null) {
          this.company_name = billingInfo.name;
          this.country_id = billingInfo.country_id;
          this.state = billingInfo.state;
          this.zip = billingInfo.zip_code;
          this.city = billingInfo.city;
          this.address = billingInfo.address;
          this.calculateTax();
        }
        this.$store.commit("hideLoading");
      })
      .catch((error) => {
        this.$store.commit("hideLoading");
      });

    const elements = window.stripe.elements();

    const elementStyles = {
      base: {
        color: "#262626",
        fontFamily: "Roboto, sans-serif",
        fontSize: "14px",
        "::placeholder": {
          color: "#999999",
        },
      },
      invalid: {
        color: "#262626",

        "::placeholder": {
          color: "#999999",
        },
      },
    };

    const elementClasses = {
      focus: "focused",
      empty: "empty",
      invalid: "invalid",
    };

    cardNumber = elements.create("cardNumber", {
      style: elementStyles,
      classes: elementClasses,
    });
    cardNumber.mount("#card-number");

    const cardExpiry = elements.create("cardExpiry", {
      style: elementStyles,
      classes: elementClasses,
    });
    cardExpiry.mount("#card-expiry");

    const cardCvc = elements.create("cardCvc", {
      style: elementStyles,
      classes: elementClasses,
    });
    cardCvc.mount("#card-cvc");

    const _this = this;
    cardNumber.addEventListener("change", function(event) {
      if (event.error) {
        _this.errorMessage = event.error.message;
      } else {
        _this.errorMessage = "";
      }
    });
  },
  methods: {
    onCountrySelected(option) {
      this.country_id = option.value;
      this.countryList = this.countries;
    },

    onCountrySearch(query) {
      this.countryList = this.countries.filter((item) => {
        if (query === undefined || query === null || query === "") {
          return item;
        }
        return item.label.toLowerCase().indexOf(query.toLowerCase()) > -1;
      });
    },

    onStateSelected(option) {
      this.state = option.value;
      this.stateList = this.getStateList();
      this.calculateTax();
    },

    onStateSearch(query) {
      this.stateList = this.getStateList().filter((item) => {
        if (query === undefined || query === null || query === "") {
          return item;
        }
        return item.label.toLowerCase().indexOf(query.toLowerCase()) > -1;
      });
    },

    onZipChange(event) {
      const _this = this;
      if (_this.debounce) {
        clearTimeout(_this.debounce);
      }
      _this.debounce = setTimeout(() => {
        _this.calculateTax();
      }, 1000);
    },

    onCityChange(event) {
      const _this = this;
      if (_this.debounce) {
        clearTimeout(_this.debounce);
      }
      _this.debounce = setTimeout(() => {
        _this.calculateTax();
      }, 1000);
    },

    onAddressChange(event) {
      const _this = this;
      if (_this.debounce) {
        clearTimeout(_this.debounce);
      }
      _this.debounce = setTimeout(() => {
        _this.calculateTax();
      }, 1000);
    },

    changePlan: function(event, plan) {
      event.preventDefault();
      event.stopPropagation();

      this.plan = plan;
      this.pricePerUser = Billing.planValue(this.plan);
      this.subTotal = this.pricePerUser * this.totalUsers;
      this.totalAmount = this.subTotal + this.taxAmount;
      this.subscribedUntil =
        Billing.isMonthly(plan) ? this.$moment().add(1, "month").format("M/D/YYYY")
          : this.$moment().add(1, "year").format("M/D/YYYY");
      this.planLabel = Billing.isMonthly(plan) ? this.label.get("month") : this.label.get("year");
      localStorage.setItem("plan", plan);
      this.calculateTax();
    },

    calculateTax() {
      if (
        !this.country_id ||
        !this.zip ||
        !this.city ||
        !this.address ||
        this.country_id !== 187
      ) {
        return false;
      }

      const country = this.countries.find((x) => x.value === this.country_id);
      const data = {
        country: country.iso_code,
        state: this.state,
        zip: this.zip,
        city: this.city,
        address: this.address,
        price_plan: this.plan,
        quantity: parseInt(this.totalUsers)
      };

      SubscriptionService.getTaxAmountForSubscription(data)
        .then((result) => {
          this.taxAmount = result.taxAmount;
          this.totalAmount = this.subTotal + this.taxAmount;
        })
        .catch((error) => {
          console.log(error);
        });
    },

    purchase() {
      this.errorMessage = '';
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.error = true;
        this.errorMessage = this.label.get('message_please_fill_in_all_required_fields');
        window.scrollTo(0, 0);
        return false;
      }

      this.disabledCompletePurchaseBtn = true;
      this.$store.commit("showLoading");

      window.stripe
        .confirmCardSetup(this.clientSecret, {
          payment_method: {
            card: cardNumber,
            billing_details: {
              name: this.card_holder,
              email: this.$store.state.user.email,
              address: {
                city: this.city,
                postal_code: this.zip,
              },
            },
          },
        })
        .then(
          (result) => {
            if (result.error) {
              this.disabledCompletePurchaseBtn = false;
              this.errorMessage = result.error.message;
              this.$store.commit("hideLoading");
            } else {
              this.paymentMethod = result.setupIntent.payment_method;
              let data = {
                plan: this.plan,
                company_name: this.company_name,
                name: this.name,
                city: this.city,
                zip: this.zip,
                address: this.address,
                country_id: this.country_id,
                state: this.state,
                tax_id: this.tax_id,
                card_holder: this.card_holder,
                paymentMethod: this.paymentMethod,
              };

              this.subscribe(data, result.setupIntent.id);
            }
          },
          (error) => {
            this.disabledCompletePurchaseBtn = false;
            console.log(error);
          }
        );
    },

    subscribe(data, setupIntentId) {
      SubscriptionService.subscribe(data).then(
          (subscription) => {
            this.payInvoice(subscription.data.latest_invoice, setupIntentId);
          },
          (error) => {
            this.$store.commit("hideLoading");
            if (error.response.data.error !== undefined) {
              this.errorMessage = error.response.data.error.message;
            } else {
              this.errorMessage = error.response.data.message;
            }
            this.disabledCompletePurchaseBtn = false;
          }
      );
    },

    payInvoice(invoiceId, transaction_id) {
      // BillingService.payInvoice({
      //   invoice_id: invoiceId,
      //   retry_count: this.payInvoiceRetryCount
      // }).then((result) => {
        localStorage.removeItem("plan");
        localStorage.removeItem("quantity");
        this.$store.commit("subscribed", true);
        this.$store.commit("onTrial", false);
        this.$store.state.company.trial_ends_at = null;
        this.$store.state.company.trial_days_left = "";
        this.$store.commit("hideLoading");

        try {
          const country = this.countries.find(
            (x) => x.value === this.country_id
          );
          window.dataLayer.push({
            event: "complete-purchase",
            conversionValue: this.totalAmount,
            totalUsers: this.totalUsers,
            plan: this.plan,
            country: country.label,
            transaction_id: transaction_id,
          });

          window.kochava.activity('Subscribe', {
            name: this.plan,
            quantity: this.totalUsers,
            sum: this.totalAmount,
            subscription_type: 'STRIPE'
          }, function(err) { });

        } catch (e) {}

        if (UtilsService.isMobile.any()) {
          this.$router.push({ path: "/settings/subscription" });
        } else {
          this.$router.push({ path: "/activity" });
        }
        setTimeout(() => {
          this.$root.$emit("showSubscriptionSuccessful");
        }, 300);
      // }).catch((error) => {
      //   if(error.response.status !== 423) {
      //     this.clientSecret = error.response.data.client_secret;
      //     this.$store.commit("hideLoading");
      //     if (error.response.data.error !== undefined) {
      //       this.errorMessage = error.response.data.error.message;
      //     } else {
      //       this.errorMessage = error.response.data.message;
      //     }
      //     this.disabledCompletePurchaseBtn = false;
      //   } else if(this.payInvoiceRetryCount < 10) {
      //     this.payInvoiceRetryCount++;
      //     setTimeout(() => {
      //       this.payInvoice(invoiceId);
      //     }, 1000);
      //   }
      // });
    },

    getStateList() {
      return [
        { value: "AL", label: "Alabama" },
        { value: "AK", label: "Alaska" },
        { value: "AZ", label: "Arizona" },
        { value: "AR", label: "Arkansas" },
        { value: "CA", label: "California" },
        { value: "CO", label: "Colorado" },
        { value: "CT", label: "Connecticut" },
        { value: "DE", label: "Delaware" },
        { value: "DC", label: "Washington D.C." },
        { value: "FL", label: "Florida" },
        { value: "GA", label: "Georgia" },
        { value: "HI", label: "Hawaii" },
        { value: "ID", label: "Idaho" },
        { value: "IL", label: "Illinois" },
        { value: "IN", label: "Indiana" },
        { value: "IA", label: "Iowa" },
        { value: "KS", label: "Kansas" },
        { value: "KY", label: "Kentucky" },
        { value: "LA", label: "Louisiana" },
        { value: "ME", label: "Maine" },
        { value: "MD", label: "Maryland" },
        { value: "MA", label: "Massachusetts" },
        { value: "MI", label: "Michigan" },
        { value: "MN", label: "Minnesota" },
        { value: "MS", label: "Mississippi" },
        { value: "MO", label: "Missouri" },
        { value: "MT", label: "Montana" },
        { value: "NE", label: "Nebraska" },
        { value: "NV", label: "Nevada" },
        { value: "NH", label: "New Hampshire" },
        { value: "NJ", label: "New Jersey" },
        { value: "NM", label: "New Mexico" },
        { value: "NY", label: "New York" },
        { value: "NC", label: "North Carolina" },
        { value: "ND", label: "North Dakota" },
        { value: "OH", label: "Ohio" },
        { value: "OK", label: "Oklahoma" },
        { value: "OR", label: "Oregon" },
        { value: "PA", label: "Pennsylvania" },
        { value: "RI", label: "Rhode Island" },
        { value: "SC", label: "South Carolina" },
        { value: "SD", label: "South Dakota" },
        { value: "TN", label: "Tennessee" },
        { value: "TX", label: "Texas" },
        { value: "UT", label: "Utah" },
        { value: "VT", label: "Vermont" },
        { value: "VA", label: "Virginia" },
        { value: "WA", label: "Washington" },
        { value: "WV", label: "West Virginia" },
        { value: "WI", label: "Wisconsin" },
        { value: "WY", label: "Wyoming" },
        { value: "AS", label: "American Samoa" },
        { value: "GU", label: "Guam" },
        { value: "MP", label: "Northern Mariana Islands" },
        { value: "PR", label: "Puerto Rico" },
        { value: "VI", label: "U.S. Virgin Islands" },
      ];
    },
  }
};
</script>

<style lang="scss">
.billing-wrapper {
  .atto-select {
    .select-heading {
      width: 100%;

      span {
        font-weight: 400 !important;
      }
    }

    .select-options {
      .ps {
        max-height: 280px;
      }
      .heading {
        .search-wrapper {
          input {
            font-weight: 400 !important;
          }
        }
      }
    }
  }
}

.StripeElement {
  width: 100%;
  border: 2px solid #efefef;
  padding: 11px;
  color: #262626;
  font-family: "Roboto", sans-serif;
  font-size: 0.875rem;
  line-height: 1rem;
  font-weight: 400;
  transition: ease all 0.15s;

  &.focused {
    border-color: #999;
  }

  &.invalid {
    border-color: #e05656;
  }
}

.atto-form {
  .field-row {
    &.no-mb {
      margin-bottom: 0;
    }

    &.title-field {
    }

    .field-wrap {
    }
  }
}

button.switch-plan {
  border: 2px solid #efefef;
  border-radius: 0;
  height: 43px;
  width: 132px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  font-size: 0.625rem;
  line-height: 14px;
  font-weight: 400;
  font-family: "Roboto", sans-serif;
  color: #ee5c65;
  text-transform: uppercase;
  text-decoration: none;
  padding: 0 6px;
  margin-left: auto;
  -webkit-transition: ease all 0.15s;
  -moz-transition: ease all 0.15s;
  -ms-transition: ease all 0.15s;
  -o-transition: ease all 0.15s;
  transition: ease all 0.15s;

  &:hover {
    border-color: #999;
  }
}

.atto-select {
  &.search-filtering {
    min-width: 210px;
    width: 100%;
  }
}

@media only screen and (max-width: 768px) and (orientation: landscape) {
  main.billing {
    .billing-wrapper {
      form {
        flex-direction: column;
        align-items: center;
      }

      .billing-container {
        width: 66.66667%;
        margin-bottom: 24px;
      }

      .billing-wrap {
        width: 66.66667%;
      }
    }
  }
}
</style>
<style lang="scss" scoped>
main.billing {
  .billing-sidebar {
    .field-row {
      &.discount {
        background-color: rgba(6, 184, 183, 0.1);
        height: 40px;
        padding: 8px;

        h5 {
          &.field-label {
            color: #00b9cb;
          }
        }

        span {
          &.value {
            color: #00b9cb;
          }
        }
      }
    }
  }
}

button {
  &.switch-plan {
    color: #262626;
    font-size: 10px;
    line-height: 16px;
    text-transform: capitalize;
    /*height: 41px;
            width: 99px;*/
    padding: 4.5px;
    border: 1px solid #666666;

    b {
      text-transform: uppercase;
      font-size: 12px;
    }

    &:hover {
      border-color: #00b9cb;
      color: #00b9cb;
    }
  }
}
</style>
